import { useState, useMemo, useCallback } from "react";
import { Button, Input } from "@aidkitorg/component-library";
import { usePost } from "../../../API";
import { useLocalizedStrings } from "../../../Localization";
import { useConnectionModal } from "./ConnectionModalContext";
import { SourceHeader } from "./SourceHeader";
import { type ConnectionModalState } from "./types";

interface ConnectionModalSourcePageProps {
  state: ConnectionModalState & { stage: "source" };
}

export function ConnectionModalSourcePage({
  state,
}: ConnectionModalSourcePageProps) {
  const L = useLocalizedStrings();
  const connectToSource = usePost("/deck/connect_to_source");
  const { token, targetField, getLocalizedString, onUpdateState } = useConnectionModal();

  const [selectedSourceFields, setSelectedSourceFields] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasFilledOutAllFields = useMemo(() => {
    return state.selectedSource.fields.every((field) => !!selectedSourceFields[field.field_name]);
  }, [state, selectedSourceFields]);

  const handleUpdateSourceField = useCallback((fieldName: string, fieldValue: string) => {
    setSelectedSourceFields((selectedSourceFields) => ({ ...selectedSourceFields, [fieldName]: fieldValue }));
  }, []);

  const handleClickGoBack = useCallback(() => {
    onUpdateState({ stage: "search" });
  }, [onUpdateState]);

  const handleClickSubmit = useCallback(async () => {
    setIsSubmitting(true);
    const { connection_id } = await connectToSource({
      target_field: targetField,
      connection_details: {
        link_token: token,
        source_id: state.selectedSource.source_id,
        fields: state.selectedSource.fields.map((field) => ({
          field_name: field.field_name,
          field_value: selectedSourceFields[field.field_name] || "",
        })),
      },
    });

    if (connection_id) {
      onUpdateState({
        stage: "pending",
        selectedSource: state.selectedSource,
        connectionId: connection_id,
      });
    } else {
      onUpdateState({
        stage: "error",
        selectedSource: state.selectedSource,
        errorStatus: "ExpiredToken",
      })
    }
  }, [state, selectedSourceFields, onUpdateState]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && hasFilledOutAllFields) {
      handleClickSubmit();
    }
  }, [handleClickSubmit, hasFilledOutAllFields]);

  return <div>
    <SourceHeader source={state.selectedSource} />
    <fieldset>
      {state.selectedSource.fields.map((field) =>
        <div className="mt-3">
          <Input
            label={field.field_label}
            value={selectedSourceFields[field.field_name] || ""}
            type={field.field_type === "Password" ? "password" : "text"}
            onChange={(e) => handleUpdateSourceField(field.field_name, e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            dir="ltr" // These fields will always be in English
            required
          />
        </div>
      )}
    </fieldset>
    <div className="mt-6 flex justify-between">
      <Button onClick={handleClickGoBack} variant="secondary">
        {getLocalizedString("returnToSearchPageText", "return_to_search_page")}
      </Button>
      <Button
        onClick={handleClickSubmit}
        isDisabled={!hasFilledOutAllFields}
        loading={isSubmitting}
        title={!hasFilledOutAllFields ? L.questions.deck_verification.connection_modal.required_fields_missing : undefined}
        variant="primary"
      >
        {L.submission.submit}
      </Button>
    </div>
  </div>;
}
